import React from 'react';
import { useNavigate } from "react-router-dom";
import { motion } from 'framer-motion';
import axios from 'axios';
import MailOutlineIcon from '@mui/icons-material/MailOutline';
import LockOutlinedIcon from '@mui/icons-material/LockOutlined';
import Visibility from '@mui/icons-material/Visibility';
import VisibilityOff from '@mui/icons-material/VisibilityOff';
import logoImg from '../../client/assets/images/Logo.png';

/* ─── Executive-minimalist tokens ───────────────────────────── */
const PRIMARY       = '#a32f2f';
const PRIMARY_DEEP  = '#82161b';
const PRIMARY_TINT  = 'rgba(163,47,47,0.10)';
const BG            = '#f9f9ff';
const SURFACE       = '#ffffff';
const BORDER        = '#e5e7eb';
const TEXT          = '#141b2b';
const TEXT_DIM      = '#585f6c';

export default function Login(props) {
  const navigate = useNavigate();

  const [PC_USR_EMAIL, setUsrEmail]     = React.useState('');
  const [PC_USR_PASSWORD, setUsrPass]  = React.useState('');
  const [emailFocus, setEmailFocus]    = React.useState(false);
  const [passFocus, setPassFocus]      = React.useState(false);
  const [showPass, setShowPass]        = React.useState(false);

  /* ── validation ── */
  const handleValidate = () => {
    if (!PC_USR_EMAIL || !PC_USR_PASSWORD) {
      props.toggleLoader(false);
      props.toggleSnackBar(true, 'Error', 'Please enter your email and password', 'error');
      return false;
    }
    return true;
  };

  /* ── submit ── */
  const handleLogin = () => {
    props.toggleLoader(true);
    if (!handleValidate()) return;
    axios
      .post(`${process.env.REACT_APP_BASE_URL}services/query/PCLogin.php`, {
        PC_USR_EMAIL,
        PC_USR_PASSWORD,
      })
      .then((res) => {
        props.toggleLoader(false);
        if (res.data.success === 1) {
          localStorage.setItem('PC_GRP_ATT',   res.data.PC_GRP_ATT);
          localStorage.setItem('PC_GRP_ID',    res.data.PC_GRP_ID);
          localStorage.setItem('PC_USR_ROLE_ID', res.data.PC_USR_ROLE_ID);
          localStorage.setItem('PC_USR_EMAIL', res.data.PC_USR_EMAIL);
          localStorage.setItem('PC_USR_NAME',  res.data.PC_USR_NAME);
          localStorage.setItem('PC_USR_ID',    res.data.PC_USR_ID);
          localStorage.setItem('PC_USR_TOKEN', res.data.PC_USR_TOKEN);
          localStorage.setItem('PC_USR_REFRESH_TOKEN', res.data.PC_USR_REFRESH_TOKEN);
          localStorage.setItem('OTHER_DATA',   JSON.stringify(res.data.OTHER_DATA));
          props.toggleSnackBar(true, 'Success', 'Welcome to Phone Clinic 4U!', 'success');
          navigate('/admin/dashboard', { replace: true });
        } else {
          props.toggleSnackBar(true, 'Error', 'Invalid credentials — please try again', 'error');
        }
      })
      .catch((err) => {
        props.toggleLoader(false);
        props.toggleSnackBar(true, 'Error', err.message || 'Server error', 'error');
        console.error('Login error:', err);
      });
  };

  const handleKeyPress = (e) => {
    if (e.key === 'Enter') handleLogin();
  };

  /* ── shared field style ── */
  const fieldStyle = (focused) => ({
    width: '100%', boxSizing: 'border-box',
    background: SURFACE,
    border: `1px solid ${focused ? PRIMARY : BORDER}`,
    borderRadius: '4px',
    padding: '11px 14px 11px 42px',
    color: TEXT, fontSize: '14px',
    outline: 'none',
    transition: 'border-color 0.2s, box-shadow 0.2s',
    boxShadow: focused ? `0 0 0 2px ${PRIMARY_TINT}` : 'none',
    fontFamily: "'Inter', sans-serif",
  });

  const labelStyle = {
    display: 'block', fontSize: '12px', fontWeight: 500,
    letterSpacing: '0.04em', color: TEXT_DIM, marginBottom: '7px',
    textTransform: 'uppercase',
  };

  /* ────────────────────────────────── render ────────────────── */
  return (
    <div
      className="pc-admin"
      style={{
        minHeight: '100vh',
        background: BG,
        display: 'flex', alignItems: 'center', justifyContent: 'center',
        padding: '24px',
        fontFamily: "'Inter', sans-serif",
      }}
    >
      <motion.div
        initial={{ opacity: 0, y: 16 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ duration: 0.45, ease: 'easeOut' }}
        style={{
          width: '100%', maxWidth: '420px',
          background: SURFACE,
          border: `1px solid ${BORDER}`,
          borderRadius: '8px',
          padding: '40px 36px 32px',
          boxShadow: '0 10px 15px -3px rgba(0,0,0,0.05)',
        }}
      >
        {/* ── Logo + brand ── */}
        <div style={{ textAlign: 'center', marginBottom: '28px' }}>
          <div style={{
            display: 'inline-flex', alignItems: 'center', justifyContent: 'center',
            width: '56px', height: '56px', borderRadius: '8px',
            background: PRIMARY, marginBottom: '16px',
          }}>
            <img
              src={logoImg}
              alt="Phone Clinic 4U Logo"
              style={{ width: '34px', height: '34px', objectFit: 'contain' }}
            />
          </div>
          <div style={{ fontSize: '22px', fontWeight: 700, color: TEXT, letterSpacing: '-0.01em', lineHeight: 1.1 }}>
            Phone Clinic <span style={{ color: PRIMARY }}>4U</span>
          </div>
          <div style={{
            fontSize: '11px', letterSpacing: '0.18em', color: TEXT_DIM,
            fontWeight: 600, marginTop: '8px', textTransform: 'uppercase',
          }}>
            Admin Portal
          </div>
        </div>

        {/* ── divider ── */}
        <div style={{ height: '1px', background: BORDER, marginBottom: '26px' }} />

        {/* ── welcome line ── */}
        <div style={{ marginBottom: '24px' }}>
          <div style={{ fontSize: '18px', fontWeight: 600, color: TEXT, marginBottom: '4px', letterSpacing: '-0.01em' }}>
            Welcome back
          </div>
          <div style={{ fontSize: '14px', color: TEXT_DIM, lineHeight: 1.5 }}>
            Sign in to access the dashboard
          </div>
        </div>

        {/* ── Email field ── */}
        <div style={{ marginBottom: '16px' }}>
          <label style={labelStyle} htmlFor="admin-email">Email Address</label>
          <div style={{ position: 'relative' }}>
            <MailOutlineIcon style={{
              position: 'absolute', left: '13px', top: '50%', transform: 'translateY(-50%)',
              color: emailFocus ? PRIMARY : '#9ca3af', transition: 'color 0.2s',
              fontSize: '19px', pointerEvents: 'none',
            }} />
            <input
              id="admin-email"
              type="email"
              name="PC_USR_EMAIL"
              value={PC_USR_EMAIL}
              autoComplete="new-password"
              placeholder="admin@phoneclinic.co.uk"
              onFocus={() => setEmailFocus(true)}
              onBlur={() => setEmailFocus(false)}
              onKeyPress={handleKeyPress}
              onChange={(e) => setUsrEmail(e.target.value)}
              style={fieldStyle(emailFocus)}
            />
          </div>
        </div>

        {/* ── Password field ── */}
        <div style={{ marginBottom: '26px' }}>
          <label style={labelStyle} htmlFor="admin-password">Password</label>
          <div style={{ position: 'relative' }}>
            <LockOutlinedIcon style={{
              position: 'absolute', left: '13px', top: '50%', transform: 'translateY(-50%)',
              color: passFocus ? PRIMARY : '#9ca3af', transition: 'color 0.2s',
              fontSize: '19px', pointerEvents: 'none',
            }} />
            <input
              id="admin-password"
              type={showPass ? 'text' : 'password'}
              name="PC_USR_PASSWORD"
              value={PC_USR_PASSWORD}
              autoComplete="new-password"
              placeholder="••••••••"
              onFocus={() => setPassFocus(true)}
              onBlur={() => setPassFocus(false)}
              onKeyPress={handleKeyPress}
              onChange={(e) => setUsrPass(e.target.value)}
              style={{ ...fieldStyle(passFocus), padding: '11px 42px 11px 42px' }}
            />
            <button
              type="button"
              onClick={() => setShowPass((p) => !p)}
              style={{
                position: 'absolute', right: '10px', top: '50%',
                transform: 'translateY(-50%)',
                background: 'none', border: 'none', cursor: 'pointer',
                color: '#9ca3af', padding: '4px', lineHeight: 0,
                display: 'flex', alignItems: 'center',
              }}
              aria-label={showPass ? 'Hide password' : 'Show password'}
            >
              {showPass ? <VisibilityOff style={{ fontSize: '19px' }} /> : <Visibility style={{ fontSize: '19px' }} />}
            </button>
          </div>
        </div>

        {/* ── Login button ── */}
        <button
          id="admin-login-btn"
          onClick={handleLogin}
          style={{
            width: '100%',
            background: PRIMARY,
            color: '#ffffff', border: 'none',
            padding: '13px 0',
            borderRadius: '8px',
            fontSize: '14px', fontWeight: 600, letterSpacing: '0.01em',
            cursor: 'pointer',
            fontFamily: "'Inter', sans-serif",
            transition: 'background 0.2s',
          }}
          onMouseEnter={(e) => (e.currentTarget.style.background = PRIMARY_DEEP)}
          onMouseLeave={(e) => (e.currentTarget.style.background = PRIMARY)}
        >
          Sign In
        </button>

        {/* ── footer note ── */}
        <div style={{ marginTop: '24px', textAlign: 'center' }}>
          <div style={{ fontSize: '12px', color: '#9ca3af', letterSpacing: '0.01em' }}>
            Restricted access — authorised personnel only
          </div>
        </div>
      </motion.div>
    </div>
  );
}
